import { Component, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MqttService } from '../../../../../../../services/mqtt.service';
import * as L from 'leaflet';
import { Subscription } from 'rxjs';

import {
  convertTimestamp,
  getENV,
  getRaceType,
} from 'src/app/_helpers/helpers';
import * as moment from 'moment-timezone';

interface Runner {
  StartNumber: number;
  BarrierNumber: string;
  ExternalSilkID: string;
  State: string;
  isLoadedChecked: boolean;
  isDNS: boolean;
  LoadedRadio: String;
  distanceToGo?: string;
  distanceTraveled?: number;
}

export interface RaceEntrant {
  ExternalSilkID: string;
  AnimalName: string;
  TrainerName: string;
  JockeyName: string;
  Latitude?: number;
  Longitude?: number;
  SCN: string;
  ExternalAnimalID: string;
  SilkURL?: string;
  RaceState?: string;
  isSelected?: boolean;
  StartNumber?: number;
}

interface RaceResponse {
  entrants: RaceEntrant[];
  code_type: string;
}

interface AdvanceLiveDataSet {
  ExternalAnimalId: string;
  SCN: string;
  X: number;
  Y: number;
  Speed: number;
  Cog: number;
  ResultState: number;
  ResultSubState: string | null;
  CurrentRank: number;
  CurrentRankIndex: number;
  DistanceToGo: string;
  DistanceToGoInRankUpdateFrequency: string;
  Lap: number;
  DistanceToRail: number;
  AverageDistanceToRail: number;
  DistanceTraveled: number;
  AverageSpeed: number;
  StrideFrequency: number;
  StrideLength: number;
  Latitude: number;
  Longitude: number;
}

interface HeartBeatMessage {
  AdvanceLiveDataSets: AdvanceLiveDataSet[];
}

@Component({
  selector: 'app-race-runner-map',
  templateUrl: './race-runner-map.component.html',
  styleUrls: ['./race-runner-map.component.css'],
  styles: [
    `
      .color-indicator {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.2);
        display: inline-block;
      }
      .custom-marker {
        background: none !important;
        border: none !important;
      }
    `,
  ],
})
export class RaceRunnerMapComponent implements OnInit, AfterViewInit {
  private map!: L.Map;
  private markers: { [key: string]: L.Marker } = {};
  advance_live_data_topic = '';
  venue_id = '';
  event_id = '';
  race_id = '';
  code_type = '';
  private entrantsMap: { [key: string]: RaceEntrant } = {};
  private initialMessageReceived = false;
  private lastMessageTime: number = 0;
  config: any;
  entrantsList: RaceEntrant[] = []; // List for the UI to show entrants
  defaultMapCenter = [-33.8688, 151.2093]; // Default to Sydney coordinates
  public runners: { [key: string]: Runner } = {};
  races: any[] = [];
  selectedRaceNumber: number | null = null;
  raceStartTime: number = 0; // Store the race start time for calculating running time
  currentTimeMs: number = 0; // Current time for race countdown
  private timerInterval: any = null; // Interval for updating current time
  layoutMode: 'vertical' | 'horizontal' = 'vertical'; // Default layout mode

  private colors: string[] = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#FF00FF',
    '#00FFFF',
    '#FFA500',
    '#800080',
    '#008000',
    '#FFC0CB',
    '#A52A2A',
    '#808080',
    '#FFD700',
    '#4B0082',
    '#7CFC00',
    '#FF69B4',
    '#CD853F',
    '#00CED1',
  ];
  public entrantColors: { [key: string]: string } = {};

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private mqttService: MqttService,
    private router: Router
  ) {
    this.config = this.route.snapshot.data['config'];
  }
  venueName: string = '';
  ngOnInit(): void {
    // Start timer for race countdown
    this.currentTimeMs =
      new Date().getTime() + new Date().getTimezoneOffset() * 60000;
    this.timerInterval = setInterval(() => {
      this.currentTimeMs =
        new Date().getTime() + new Date().getTimezoneOffset() * 60000;
    }, 1000);

    // Get race info
    this.route.params.subscribe((params) => {
      this.venue_id = params['venue_id'];
      this.event_id = params['event_id'];
      this.race_id = params['race_id'];

      // Start fetching data
      this.fetchRaces();
    });
  }

  setLayout(mode: 'vertical' | 'horizontal'): void {
    this.layoutMode = mode;
    // Trigger map resize after layout change to ensure proper rendering
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);
  }

  async fetchRaces() {
    // Clean up any existing subscriptions when parameters change
    this.cleanupCurrentSubscription();

    if (this.races.length == 0) {
      await this.loadRacesBasedOnEvent();
    }
    const raceDetailsUrl = `${
      this.config[getENV()].raceAPI
    }/race-entrant-details?ExternalRaceId=${this.race_id}&ExternalEventId=${
      this.event_id
    }`;

    if (this.races.length > 0) {
      const selectedRace = this.races.find((race: any) => {
        return race['ExternalRaceId'] === this.race_id;
      });
      if (selectedRace) {
        this.selectedRaceNumber = Number(selectedRace.SquentialRaceOrderNumber);
        this.selectedRace = selectedRace;
      } else {
        this.selectedRaceNumber = null;
      }
    }
    this.advance_live_data_topic = `TRACKING/AUS/${this.venue_id}/${this.event_id}/${this.race_id}/AdvanceLiveData`;

    // Initialize map immediately with default center
    this.initMap(this.defaultMapCenter[0], this.defaultMapCenter[1]);

    // Fetch race details
    this.http.get<RaceResponse>(raceDetailsUrl).subscribe(
      (data: RaceResponse) => {
        // Initialize runners first so we can check isDNS status
        data.entrants.forEach((entrant) => {
          this.runners[entrant.ExternalAnimalID] = {
            StartNumber: entrant.StartNumber || 0,
            BarrierNumber: entrant.SCN,
            ExternalSilkID: entrant.ExternalSilkID,
            State: entrant.RaceState || '',
            isLoadedChecked: false,
            isDNS: entrant.RaceState === 'DNS',
            LoadedRadio: '',
          };
        });

        // Process entrants with proper initial selection state
        this.entrantsList = data.entrants.map((e) => ({
          ...e,
          isSelected: !this.runners[e.ExternalAnimalID]?.isDNS,
        }));

        this.selectedRace['entrants'] = this.entrantsList;

        this.mapEntrants(data.entrants);
        this.assignEntrantColors();
        this.addMarkers(this.entrantsList.filter((e) => e.isSelected)); // Only add markers for selected entrants
        this.connect();
        this.code_type = data.code_type;
        this.venueName = data['venue'];
      },
      (error) => {
        console.error('Error fetching race details:', error);
      }
    );
  }

  selectedRace: any = null;
  async loadRacesBasedOnEvent(): Promise<void> {
    const apiURL = `${
      this.config[getENV()].raceAPI
    }/getracedetails?ExternalVenueId=${this.venue_id}&ExternalEventId=${
      this.event_id
    }`;

    try {
      const result: any = await this.http.get(apiURL).toPromise();
      if (result && Array.isArray(result) && result.length > 0) {
        result.forEach((race: any) => {
          race['RaceTimeStr'] = convertTimestamp(race['RaceTime'], '');

          // Add RaceTimeMs for countdown calculation - use moment to parse the date consistently
          try {
            race['RaceTimeMs'] = moment(
              race['RaceTime'],
              'YYYY-MM-DD-HH:mm:ss'
            ).valueOf();
          } catch (error) {
            console.error('Error parsing race time:', error);
            race['RaceTimeMs'] = 0;
          }
        });
        this.races = result.sort((a: any, b: any) => {
          return a['SquentialRaceOrderNumber'] - b['SquentialRaceOrderNumber'];
        });
        if (this.races.length > 0) {
          const selectedRace = this.races.find(
            (race) => race.ExternalRaceId === this.race_id
          );
          this.selectedRace = selectedRace;
          this.selectedRaceNumber = selectedRace
            ? Number(selectedRace.SquentialRaceOrderNumber)
            : null;

          // Set race start time for running time calculations
          if (selectedRace && selectedRace.RaceTime) {
            try {
              // Parse the race time from the API response
              const raceTime = new Date(selectedRace.RaceTime);
              this.raceStartTime = raceTime.getTime();

              // Store the parsed date for reference
              selectedRace.RaceTimeDate = raceTime;
            } catch (error) {
              console.error('Error parsing race time:', error);
            }
          }
        } else {
          this.selectedRaceNumber = null;
        }
      }
    } catch (error) {
      console.error('Error fetching races:', error);
      this.races = [];
    }
  }

  prev(): void {
    const currentIndex = this.races.findIndex(
      (race) => race.ExternalRaceId === this.race_id
    );
    if (currentIndex > 0) {
      const previousRace = this.races[currentIndex - 1];
      this.navigator(previousRace.ExternalRaceId);
    }
  }

  next(): void {
    const currentIndex = this.races.findIndex(
      (race) => race.ExternalRaceId === this.race_id
    );
    if (currentIndex < this.races.length - 1) {
      const nextRace = this.races[currentIndex + 1];
      this.navigator(nextRace.ExternalRaceId);
    }
  }
  private cleanupCurrentSubscription(): void {
    if (this.mqttService.client && this.advance_live_data_topic) {
      this.mqttService.client.off('message', this.messageHandler);
      this.mqttService.unsubscribe(this.advance_live_data_topic);
      this.mqttService.subscribe(
        `TRACKING/+/${this.selectedRace.ExternalVenueId}/${this.selectedRace.ExternalEventId}/${this.selectedRace.ExternalRaceId}/SectionTimes`
      );
    }

    // Clear all markers from the map
    Object.values(this.markers).forEach((marker) => {
      this.map.removeLayer(marker);
    });
    this.markers = {};
    this.initialMessageReceived = false;
  }

  navigator(raceId: string): void {
    const venue_id = this.venue_id;
    const event_id = this.event_id;

    // Clean up current subscriptions before navigating
    this.cleanupCurrentSubscription();

    // Use Angular router to navigate instead of page reload
    this.router.navigate(['/dash/race-runner-map', venue_id, event_id, raceId]);
  }

  ngAfterViewInit(): void {
    // Remove this as map is initialized in ngOnInit
  }

  toggleEntrant(entrant: RaceEntrant): void {
    // Don't allow toggling if the horse is scratched
    const runner = this.runners[entrant.ExternalAnimalID];
    if (runner?.isDNS) {
      return;
    }

    entrant.isSelected = !entrant.isSelected;
    if (!entrant.isSelected && this.markers[entrant.ExternalAnimalID]) {
      this.map.removeLayer(this.markers[entrant.ExternalAnimalID]);
      delete this.markers[entrant.ExternalAnimalID];
    } else if (entrant.isSelected) {
      // Re-add marker if we have position data
      const existingEntrant = this.entrantsMap[entrant.ExternalAnimalID];
      if (
        existingEntrant &&
        existingEntrant.Latitude &&
        existingEntrant.Longitude
      ) {
        this.addSingleMarker(existingEntrant);
      }
    }
  }
  private updateMarkerContent(
    marker: L.Marker,
    entrant: RaceEntrant,
    dataSet?: AdvanceLiveDataSet
  ): void {
    const popup = marker.getPopup();
    const content = `
      <div class="marker-popup">
        <h4>${entrant.AnimalName}</h4>
        <p>ID: ${entrant.ExternalAnimalID}</p>
        <p>SCN: ${dataSet ? dataSet.SCN : entrant.SCN}</p>
        ${
          dataSet
            ? `
            <hr>
            <div class="race-stats">
              <strong>Race Stats:</strong><br>
              Rank: ${dataSet.CurrentRank}<br>
              Speed: ${dataSet.Speed} m/s<br>
              Distance to Go: ${
                dataSet.DistanceToGo === 'NaN'
                  ? '-'
                  : dataSet.DistanceToGo + 'm'
              }<br>
              Distance Travelled: ${
                dataSet.DistanceTraveled === undefined ||
                isNaN(dataSet.DistanceTraveled)
                  ? '-'
                  : dataSet.DistanceTraveled + 'm'
              }<br>
              ${dataSet.Lap > 0 ? `Lap: ${dataSet.Lap}<br>` : ''}
              Latitude: ${dataSet.Latitude}<br> Longitude: ${dataSet.Longitude}
            </div>
          `
            : ''
        }
      </div>
    `;

    if (popup) {
      popup.setContent(content);
      if (popup.isOpen()) {
        popup.update();
      }
    } else {
      marker.bindPopup(content);
    }
  }

  private addSingleMarker(
    entrant: RaceEntrant,
    dataSet?: AdvanceLiveDataSet
  ): void {
    if (
      !this.map ||
      !entrant.isSelected ||
      !entrant.Latitude ||
      !entrant.Longitude
    ) {
      return;
    } // Remove existing marker if it exists
    if (this.markers[entrant.ExternalAnimalID]) {
      this.map.removeLayer(this.markers[entrant.ExternalAnimalID]);
      delete this.markers[entrant.ExternalAnimalID];
    }

    const silkUrl = this.getSilkUrl(entrant);
    // Make sure we have a color assigned
    if (!this.entrantColors[entrant.ExternalAnimalID]) {
      this.assignEntrantColors(); // Reassign colors if missing
    }
    const color =
      this.entrantColors[entrant.ExternalAnimalID] || this.colors[0];

    // Create colored circle marker with silk icon
    const icon = L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background-color: ${color};
          border: 2px solid white;
          border-radius: 50%;
          width: 12px;
          height: 12px;
          box-shadow: 0 0 5px rgba(0,0,0,0.5);">
        </div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      popupAnchor: [0, -10],
    });

    const marker = L.marker([entrant.Latitude, entrant.Longitude], {
      icon,
      zIndexOffset: 1000, // Ensure markers are above other map elements
    }).addTo(this.map);

    this.updateMarkerContent(marker, entrant, dataSet);
    this.markers[entrant.ExternalAnimalID] = marker;
  }

  private initMap(lat: number, long: number): void {
    if (this.map) {
      // If map already exists, just set the view
      this.map.setView([lat, long], 17);
      return;
    }
    this.map = L.map('map', {
      center: [lat, long],
      zoom: 17,
      minZoom: 14, // Allow a bit more zoom out for better overview
      maxZoom: 19,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
      maxNativeZoom: 19,
    }).addTo(this.map);

    // Add a handler for when all tiles are loaded
    this.map.on('load', () => {
      if (Object.keys(this.markers).length > 0) {
        this.recenterMap();
      }
    });
  }

  // Corrected recenter button functionality with better bounds handling
  recenterMap() {
    if (Object.keys(this.markers).length > 0) {
      const markerArray = Object.values(this.markers);
      const bounds = L.latLngBounds(
        markerArray.map((marker) => marker.getLatLng())
      );

      // Add a margin to the bounds
      const margin = 0.001; // roughly 100 meters
      bounds.extend([bounds.getSouth() - margin, bounds.getWest() - margin]);
      bounds.extend([bounds.getNorth() + margin, bounds.getEast() + margin]);

      this.map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 17, // Prevent zooming in too close
        animate: true,
        duration: 0.5, // Half second animation
      });
    }
  }

  private addMarkers(entrants: RaceEntrant[]): void {
    if (this.map) {
      // Clear existing markers
      Object.values(this.markers).forEach((marker) => {
        this.map.removeLayer(marker);
      });
      this.markers = {};

      for (const entrant of entrants) {
        if (entrant.isSelected !== false) {
          // Show by default if not explicitly deselected
          this.addSingleMarker(entrant);
        }
      }
    }
  }
  private updateMarkers(advanceLiveDataSets: any[]): void {
    let isFirstUpdate = !this.initialMessageReceived;

    advanceLiveDataSets.forEach((dataSet) => {
      if (!this.isValidCoordinate(dataSet.Latitude, dataSet.Longitude)) {
        return;
      } // Handle Barrier marker separately
      if (dataSet.ExternalAnimalId === 'Barrier') {
        this.updateBarrierMarker(dataSet);
        return;
      } // Update runner data with live tracking information
      if (this.runners[dataSet.ExternalAnimalId]) {
        // Update runner with live data - validate distance values

        // Handle distanceToGo - check if valid number
        const distanceToGo = parseFloat(dataSet.DistanceToGo);
        this.runners[dataSet.ExternalAnimalId].distanceToGo = !isNaN(
          distanceToGo
        )
          ? distanceToGo.toString()
          : '-';

        // Handle distanceTraveled - check if valid number
        const distanceTraveled = parseFloat(dataSet.DistanceTraveled);
        this.runners[dataSet.ExternalAnimalId].distanceTraveled = !isNaN(
          distanceTraveled
        )
          ? distanceTraveled
          : null;
      }

      const marker = this.markers[dataSet.ExternalAnimalId];
      if (marker) {
        // Update existing marker
        marker.setLatLng([dataSet.Latitude, dataSet.Longitude]);
        const entrant = this.entrantsMap[dataSet.ExternalAnimalId];
        if (entrant) {
          this.updateMarkerContent(marker, entrant, dataSet);
        }
      } else {
        // Create new entrant and marker
        const newEntrant: RaceEntrant = {
          ExternalSilkID: dataSet.ExternalAnimalId,
          AnimalName: `Runner ${dataSet.SCN}`,
          TrainerName: '',
          JockeyName: '',
          SCN: dataSet.SCN,
          ExternalAnimalID: dataSet.ExternalAnimalId,
          StartNumber: parseInt(dataSet.SCN) || undefined,
          isSelected: true,
          Latitude: dataSet.Latitude,
          Longitude: dataSet.Longitude,
        };

        if (!this.entrantsMap[dataSet.ExternalAnimalId]) {
          this.entrantsMap[dataSet.ExternalAnimalId] = newEntrant;
          this.entrantsList.push(newEntrant);
          this.assignEntrantColors(); // Reassign colors for new entrant
        }

        this.addSingleMarker(newEntrant, dataSet);
      }
    });

    // Only center map on first update
    if (isFirstUpdate && Object.keys(this.markers).length > 0) {
      this.recenterMap();
      this.initialMessageReceived = true;
    }
  }

  private lastProcessTime = 0;
  private readonly defaultThrottleInterval = 1000; // Default 1 second
  public throttleInterval = this.defaultThrottleInterval; // Public for template binding
  public readonly minThrottle = 10;
  public readonly maxThrottle = 2000;
  private messageHandler = (topic: string, payload: Buffer) => {
    if (topic === this.advance_live_data_topic) {
      const now = Date.now();
      if (now - this.lastProcessTime >= this.throttleInterval) {
        try {
          const message: HeartBeatMessage = JSON.parse(payload.toString());
          if (message?.AdvanceLiveDataSets?.length > 0) {
            this.updateMarkers(message.AdvanceLiveDataSets);
          }
        } catch (err) {
          // Silently handle parsing errors
        }
        this.lastProcessTime = now;
      }
    }
  };

  // Method to calculate map center
  private calculateMapCenter(): L.LatLng {
    const markerArray = Object.values(this.markers);
    const latitudes = markerArray.map((m) => m.getLatLng().lat);
    const longitudes = markerArray.map((m) => m.getLatLng().lng);
    const avgLatitude =
      latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length;
    const avgLongitude =
      longitudes.reduce((sum, lon) => sum + lon, 0) / longitudes.length;
    return L.latLng(avgLatitude, avgLongitude);
  }

  private connect(): void {
    // Subscribe to the MQTT topic for advance live data
    this.mqttService.subscribe(this.advance_live_data_topic);

    // Add message handler
    this.mqttService.client.on('message', this.messageHandler);

    this.mqttService.subscribe(
      `TRACKING/+/${this.selectedRace.ExternalVenueId}/${this.selectedRace.ExternalEventId}/${this.selectedRace.ExternalRaceId}/SectionTimes`
    );
    this.mqttService.client.on('message', (topic: string, payload: Buffer) => {
      const message = JSON.parse(payload.toString());
      if (message.ExternalRaceId != this.selectedRace.ExternalRaceId) {
        return;
      }
      if (topic.includes('SectionTimes')) {
        this.selectedRace['RaceState'] = 'RUNNING';
        let animal = this.selectedRace['entrants'].find((animal: any) => {
          return animal['ExternalAnimalID'] == message['ExternalAnimalId'];
        });
        if (animal) {
          if (!animal['sections']) {
            animal['sections'] = [];
          }
          if (!animal['sectionsCompleted']) {
            animal['sectionsCompleted'] = [];
          }
          if (!this.selectedRace['raceHeaders']) {
            this.selectedRace['raceHeaders'] = [];
          }
          if (!this.selectedRace['raceHeadersMain']) {
            this.selectedRace['raceHeadersMain'] = [];
          }
          const raceLength = parseInt(this.selectedRace['RaceLength']);
          message['SectionItemSet'].forEach((section: any) => {
            animal['Rank'] = section['Rank'];
            section['SectionTime'] = section['SectionTime'].replace('.', ':');
            section['IntermediateTime'] = section['IntermediateTime'].replace(
              '.',
              ':'
            );
            let sectionName: string = '';
            let [hours, minutes, seconds, milliseconds] =
              section['SectionTime'].split(':');
            const sectionTime = moment
              .duration({
                hours: parseInt(hours),
                minutes: parseInt(minutes),
                seconds: parseInt(seconds),
                milliseconds: parseInt(milliseconds.substring(0, 3)),
              })
              .asMilliseconds();

            [hours, minutes, seconds, milliseconds] =
              section['IntermediateTime'].split(':');
            const intermediateTime = moment
              .duration({
                hours: parseInt(hours),
                minutes: parseInt(minutes),
                seconds: parseInt(seconds),
                milliseconds: parseInt(milliseconds.substring(0, 3)),
              })
              .asMilliseconds();

            if (
              getRaceType(this.selectedRace['ExternalVenueId'])[
                'raceTypeCode'
              ] == 'TH'
            ) {
              const sectionNumber = parseInt(
                section['SectionName'].replace('m', '')
              );
              if (animal['sectionsCompleted'].indexOf(sectionNumber) > -1) {
                return;
              }
              if (animal['sections'].length == 0) {
                sectionName = `Start - ${raceLength - sectionNumber}m`;
              } else if (sectionNumber < raceLength) {
                sectionName = `${raceLength - sectionNumber + 200}m - ${
                  raceLength - sectionNumber
                }m`;
              } else {
                sectionName = `${raceLength - sectionNumber + 200}m - Finish`;
              }
              if (this.selectedRace['raceHeaders'].indexOf(sectionName) < 0) {
                this.selectedRace['raceHeaders'].push(sectionName);
              }
              let section_ = animal['sections'].find((section_: any) => {
                return section_['sectionName'] == sectionName;
              });
              if (!section_) {
                animal['sections'].push({
                  sectionName: sectionName,
                  sectionTime: moment.utc(sectionTime).format('m:ss.SS'),
                  sectionTimeMs: sectionTime,
                  intermediateTime: moment
                    .utc(intermediateTime)
                    .format('m:ss.SS'),
                  intermediateTimeMs: intermediateTime,
                });
                animal['sectionsCompleted'].push(sectionNumber);
              }
            } else if (
              getRaceType(this.selectedRace['ExternalVenueId'])[
                'raceTypeCode'
              ] == 'HR'
            ) {
              const TimingLineId = section['TimingLineId'];
              if (TimingLineId != undefined) {
                sectionName = (function () {
                  if (TimingLineId == 0) {
                    return `Lead Time`;
                  } else if (TimingLineId == 1) {
                    return `1st Quarter`;
                  } else if (TimingLineId == 2) {
                    return `2nd Quarter`;
                  } else if (TimingLineId == 3) {
                    return `3rd Quarter`;
                  } else {
                    return `4th Quarter`;
                  }
                })();
                if (animal['sectionsCompleted'].indexOf(sectionName) > -1) {
                  return;
                }
                if (this.selectedRace['raceHeaders'].indexOf(sectionName) < 0) {
                  this.selectedRace['raceHeaders'].push(sectionName);
                }
                let section_ = animal['sections'].find((section_: any) => {
                  return section_['sectionName'] == sectionName;
                });
                // section_['sectionTimeMs'] = sectionTime;
                // section_['intermediateTimeMs'] = intermediateTime;
                // section_['sectionTime'] = moment
                //   .utc(section_['sectionTimeMs'])
                //   .format('m:ss.SS');
                // section_['intermediateTime'] = moment
                //   .utc(section_['intermediateTimeMs'])
                //   .format('m:ss.SS');
                if (!section_) {
                  sectionName = section['SectionName'];
                  animal['sections'].push({
                    sectionName: sectionName,
                    sectionTime: moment.utc(sectionTime).format('m:ss.SS'),
                    sectionTimeMs: sectionTime,
                    intermediateTime: moment
                      .utc(intermediateTime)
                      .format('m:ss.SS'),
                    intermediateTimeMs: intermediateTime,
                  });
                  animal['sectionsCompleted'].push(sectionName);
                }
              }
            } else {
              animal['sections'].push({
                sectionName: sectionName,
                sectionTime: moment.utc(sectionTime).format('m:ss.SS'),
                sectionTimeMs: sectionTime,
                intermediateTime: moment
                  .utc(intermediateTime)
                  .format('m:ss.SS'),
                intermediateTimeMs: intermediateTime,
              });
            }
          });
        }
        let firstEntrant = this.selectedRace['entrants'][0];
        if (firstEntrant['sections']) {
          let firstEntrantLastSection: any =
            firstEntrant['sections'][firstEntrant['sections'].length - 1];
          let section = this.selectedRace['raceHeadersMain'].find(
            (section_: any) => {
              return (
                section_['sectionName'] ==
                firstEntrantLastSection['sectionName']
              );
            }
          );
          if (section) {
            section = {
              ...section,
              ...{
                sectionTime: firstEntrantLastSection.sectionTime,
                sectionTimeMs: firstEntrantLastSection.sectionTimeMs,
                intermediateTime: firstEntrantLastSection.intermediateTime,
                intermediateTimeMs: firstEntrantLastSection.intermediateTimeMs,
              },
            };
          } else {
            this.selectedRace['raceHeadersMain'].push({
              sectionName: firstEntrantLastSection.sectionName,
              sectionTime: firstEntrantLastSection.sectionTime,
              sectionTimeMs: firstEntrantLastSection.sectionTimeMs,
              intermediateTime: firstEntrantLastSection.intermediateTime,
              intermediateTimeMs: firstEntrantLastSection.intermediateTimeMs,
            });
          }
        }
      }
    });
  }
  ngOnDestroy(): void {
    // Clean up MQTT subscription
    if (this.mqttService.client) {
      this.mqttService.client.off('message', this.messageHandler);
      this.mqttService.unsubscribe(this.advance_live_data_topic);
      this.mqttService.subscribe(
        `TRACKING/+/${this.selectedRace.ExternalVenueId}/${this.selectedRace.ExternalEventId}/${this.selectedRace.ExternalRaceId}/SectionTimes`
      );
    }

    // Clean up timer interval
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  getOverallTime(race: any, entrant): string {
    if (!entrant.sections || entrant.sections.length == 0) return '-';
    let totalmilliseconds: number = 0;
    entrant.sections.forEach((section: any) => {
      totalmilliseconds += section.sectionTimeMs;
    });

    return moment.utc(totalmilliseconds).format('m:ss.SS');
  }

  private mapEntrants(entrants: RaceEntrant[]): void {
    this.entrantsMap = {};
    for (const entrant of entrants) {
      this.entrantsMap[entrant.ExternalAnimalID] = entrant;
    }
  }
  private assignEntrantColors(): void {
    // Sort entrants by start number for consistent color assignment
    const sortedEntrants = Object.values(this.entrantsMap).sort(
      (a, b) => (a.StartNumber || 0) - (b.StartNumber || 0)
    );

    // Assign colors sequentially from the colors array
    sortedEntrants.forEach((entrant, index) => {
      this.entrantColors[entrant.ExternalAnimalID] =
        this.colors[index % this.colors.length];
    });
  }
  public getSilkUrl(entrant: RaceEntrant): string {
    if (this.code_type === 'HARNESS') {
      return `https://tsd-prd01-silks.s3.ap-southeast-2.amazonaws.com/Harness_Racing_Australia/${entrant.StartNumber}.png`;
    } else if (this.venue_id.includes('STC')) {
      return `https://tsd-prd01-silks.s3.ap-southeast-2.amazonaws.com/singapore/${entrant.ExternalSilkID}.png`;
    } else if (this.venue_id.startsWith('GR')) {
      return `https://tsd-prd01-silks.s3.ap-southeast-2.amazonaws.com/Greyhounds/${entrant.StartNumber}.png`;
    } else {
      return `https://tsd-prd01-silks.s3.ap-southeast-2.amazonaws.com/Racing_Australia/${entrant.ExternalSilkID}.png`;
    }
  }

  private isValidCoordinate(
    lat: number | undefined,
    lng: number | undefined
  ): boolean {
    if (typeof lat !== 'number' || typeof lng !== 'number') return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  private findEntrantBySCN(scn: string): RaceEntrant | undefined {
    return Object.values(this.entrantsMap).find(
      (entrant) => entrant.SCN === scn
    );
  }

  updateThrottleInterval(event: any): void {
    this.throttleInterval = parseInt(event.target.value);
  }

  private updateBarrierMarker(data: any): void {
    // Remove barrier marker
    const barrierMarkerId = 'Barrier';
    if (this.markers[barrierMarkerId]) {
      this.map.removeLayer(this.markers[barrierMarkerId]);
      delete this.markers[barrierMarkerId];
    }
  }
}
